import {FC, useEffect, useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import AsyncSelect from 'react-select/async'
import type {StylesConfig} from 'react-select'
import {getClientsPageForSelect} from '../../../clients/components/core/_requests'

type ClientOption = {
  value: number
  label: string
  isCurrent?: boolean
}

export type CloneProjectPayload = {
  name: string
  clientId: number
  includeOpenTasks: boolean
  includeDeliveredTasks: boolean
}

type Props = {
  show: boolean
  onHide: () => void
  onSubmit: (payload: CloneProjectPayload) => Promise<void> | void
  projectName?: string
  currentClientId?: number
  currentClientName?: string
  isSubmitting?: boolean
}

const CLIENTS_PAGE_SIZE = 15

const selectStyles: StylesConfig<ClientOption, false> = {
  menuPortal: (base) => ({...base, zIndex: 9999}),
  control: (styles, state) => ({
    ...styles,
    minHeight: '44px',
    border: '0',
    borderRadius: '0.75rem',
    backgroundColor: 'var(--bs-gray-100)',
    boxShadow: state.isFocused ? '0 0 0 1px var(--bs-primary)' : 'none',
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: '6px 12px',
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--bs-body-color)',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: 'var(--bs-body-color)',
  }),
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--bs-gray-600)',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: 'var(--bs-body-bg)',
    border: '1px solid var(--bs-gray-300)',
    overflow: 'hidden',
  }),
  option: (styles, {isFocused, isSelected}) => ({
    ...styles,
    backgroundColor: isSelected
      ? 'rgba(54, 155, 255, 0.28)'
      : isFocused
        ? 'rgba(54, 155, 255, 0.16)'
        : 'transparent',
    color: 'var(--bs-body-color)',
    paddingTop: 10,
    paddingBottom: 10,
  }),
}

const normalizeString = (value: string) =>
  value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase()

const CloneProjectModal: FC<Props> = ({
  show,
  onHide,
  onSubmit,
  projectName,
  currentClientId,
  currentClientName,
  isSubmitting = false,
}) => {
  const [name, setName] = useState('')
  const [selectedClient, setSelectedClient] = useState<ClientOption | null>(null)
  const [includeOpenTasks, setIncludeOpenTasks] = useState(true)
  const [includeDeliveredTasks, setIncludeDeliveredTasks] = useState(true)
  const [nameError, setNameError] = useState<string | null>(null)
  const [clientError, setClientError] = useState<string | null>(null)
  const [taskTypeError, setTaskTypeError] = useState<string | null>(null)

  useEffect(() => {
    if (!show) {
      return
    }

    setName(projectName ? `Cópia - ${projectName}` : '')
    setSelectedClient(
      currentClientId && currentClientName
        ? {value: currentClientId, label: currentClientName, isCurrent: true}
        : null
    )
    setIncludeOpenTasks(true)
    setIncludeDeliveredTasks(true)
    setNameError(null)
    setClientError(null)
    setTaskTypeError(null)
  }, [show, projectName, currentClientId, currentClientName])

  const loadClientOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      const clients = await getClientsPageForSelect(1, CLIENTS_PAGE_SIZE, inputValue)
      const mapped = clients.map((client: {id: number; name: string}) => ({
        value: client.id,
        label:
          client.id === currentClientId && currentClientName
            ? `${client.name} (atual)`
            : client.name,
        isCurrent: client.id === currentClientId,
      }))

      if (
        selectedClient &&
        !mapped.some((option) => option.value === selectedClient.value) &&
        (!inputValue || normalizeString(selectedClient.label).includes(normalizeString(inputValue)))
      ) {
        return [selectedClient, ...mapped]
      }

      return mapped
    } catch {
      return []
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    onHide()
  }

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault()

    const trimmedName = name.trim()
    const hasTaskType = includeOpenTasks || includeDeliveredTasks

    setNameError(trimmedName ? null : 'Informe o nome do novo projeto.')
    setClientError(selectedClient ? null : 'Selecione o cliente do novo projeto.')
    setTaskTypeError(hasTaskType ? null : 'Marque ao menos um tipo de tarefa para copiar.')

    if (!trimmedName || !selectedClient || !hasTaskType) {
      return
    }

    try {
      await onSubmit({
        name: trimmedName,
        clientId: selectedClient.value,
        includeOpenTasks,
        includeDeliveredTasks,
      })

      onHide()
    } catch {
      // O parent já exibe feedback; mantemos o modal aberto para nova tentativa.
    }
  }

  return (
    <Modal show={show} onHide={handleClose} centered backdrop={isSubmitting ? 'static' : true}>
      <Modal.Header closeButton={!isSubmitting} className='border-0 pb-0'>
        <Modal.Title className='d-flex align-items-center gap-2'>
          <span className='symbol symbol-40px'>
            <span className='symbol-label bg-light-primary text-primary'>
              <i className='bi bi-files fs-2'></i>
            </span>
          </span>
          <span>Clonar projeto</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className='pt-4'>
        <div className='rounded border border-gray-300 border-dashed bg-light-primary p-4 mb-5'>
          <div className='fw-semibold text-gray-900 mb-1'>Crie uma nova copia com mais controle</div>
          <div className='text-muted fs-7 mb-0'>
            Defina o nome, escolha o cliente de destino e selecione quais tarefas devem ser copiadas.
          </div>
        </div>

        <Form onSubmit={handleSubmit}>
          <Form.Group className='mb-5'>
            <Form.Label className='fw-bold text-gray-900'>Nome do novo projeto</Form.Label>
            <Form.Control
              type='text'
              className='form-control form-control-solid'
              placeholder='Ex.: Cópia - Meu projeto'
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (nameError) setNameError(null)
              }}
              autoFocus
              maxLength={255}
              disabled={isSubmitting}
            />
            {nameError && <div className='text-danger fs-8 mt-2'>{nameError}</div>}
          </Form.Group>

          <Form.Group className='mb-5'>
            <Form.Label className='fw-bold text-gray-900'>Cliente do novo projeto</Form.Label>
            <AsyncSelect<ClientOption, false>
              value={selectedClient}
              defaultOptions
              cacheOptions={false}
              loadOptions={loadClientOptions}
              onChange={(option) => {
                setSelectedClient(option)
                if (clientError) setClientError(null)
              }}
              isDisabled={isSubmitting}
              placeholder='Digite para buscar um cliente'
              noOptionsMessage={() => 'Nenhum cliente encontrado'}
              loadingMessage={() => 'Buscando clientes...'}
              menuPortalTarget={document.body}
              menuPosition='fixed'
              styles={selectStyles}
              classNames={{
                control: () => 'form-control form-control-solid border-0 rounded-3 p-1 bg-gray-100',
                menu: () => 'bg-body',
              }}
              formatOptionLabel={(option) => (
                <div className='d-flex align-items-center justify-content-between gap-3'>
                  <span className='text-truncate'>{option.label}</span>
                  {option.isCurrent && (
                    <span className='badge badge-light-primary flex-shrink-0'>Atual</span>
                  )}
                </div>
              )}
            />
            {clientError && <div className='text-danger fs-8 mt-2'>{clientError}</div>}
          </Form.Group>

          <Form.Group>
            <Form.Label className='fw-bold text-gray-900'>Tarefas a copiar</Form.Label>
            <div className='d-flex flex-column gap-3 rounded bg-light p-4'>
              <Form.Check
                id='clone-project-include-open'
                type='switch'
                label='Tarefas abertas'
                checked={includeOpenTasks}
                onChange={(e) => {
                  setIncludeOpenTasks(e.target.checked)
                  if (taskTypeError) setTaskTypeError(null)
                }}
                disabled={isSubmitting}
              />
              <Form.Check
                id='clone-project-include-delivered'
                type='switch'
                label='Tarefas entregues'
                checked={includeDeliveredTasks}
                onChange={(e) => {
                  setIncludeDeliveredTasks(e.target.checked)
                  if (taskTypeError) setTaskTypeError(null)
                }}
                disabled={isSubmitting}
              />
            </div>
            {taskTypeError && <div className='text-danger fs-8 mt-2'>{taskTypeError}</div>}
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer className='border-0 pt-0'>
        <Button variant='light' onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button variant='primary' onClick={() => void handleSubmit()} disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner animation='border' size='sm' className='me-2' />
              Clonando...
            </>
          ) : (
            'Clonar projeto'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export {CloneProjectModal}
